import _get from 'lodash/get'
import _assign from 'lodash/assign'
import _cloneDeep from 'lodash/cloneDeep'
import _size from 'lodash/size'
import _gt from 'lodash/gt'
import _parseInt from 'lodash/parseInt'
import { List } from 'immutable'
import {
  post as apiPost,
  TYPE_BROOKLYN_JSON,
} from '../../api-client/index'
import { log as logError } from '../../log/error'
import { get as getServerTime } from '../server-time/index'
import { LIVE_ACCESS_SUBSCRIPTION_CODE } from '../live-access-events/constants'

export const SUBSCRIPTION_CODE_MONTHLY = 253
export const SUBSCRIPTION_CODE_ANNUAL = 280
export const SUBSCRIPTION_CODE_EVENTS_PLUS = 290
export const SUBSCRIPTION_CODE_GAIA_PLUS = 200
export const SUBSCRIPTION_CODE_PURCHASE_ONLY = 222
export const ORACLE_ACCESS_SUBSCRIPTION_CODES = [200, 253, 280, 290]

export async function compUserSubscription (options) {
  // apiPost(url, data, options, clientType, client)
  const { auth } = options

  /**
   * /billing/comp returns data in this structure:
    {
      days:     int
      period:   string
      reason:   string
      start:    date string
      success:  boolean
      uuid:     string uuid
    }
  */
  try {
    const res = await apiPost('v1/user/subscription/comp', null, { auth }, TYPE_BROOKLYN_JSON)
    if (res.status >= 200 && res.status <= 299) {
      return {
        success: res.body.success || false,
        processing: false,
        compId: res.body.uuid || '',
      }
    }
    try {
      const bodyString = JSON.stringify(res.body)
      logError('Subscription compUserSubscription received a non 200 level repsonse.', bodyString)
    } catch (e) {
      logError('Subscription compUserSubscription could not stringify response body.')
    }
    const body = _get(res, 'body', {})
    return handleCompError(body)
  } catch (e) {
    return handleCompError({})
  }
}

const SCHEMA_COMP_ERROR = {
  success: false,
  errors: null,
}

function handleCompError (body) {
  const errors = _get(body, 'errors', {})
  return _assign(_cloneDeep(SCHEMA_COMP_ERROR), {
    errors,
  })
}

export const SUBSCRIPTION_OFFERINGS_AVAILABILITY_IS_FREE = 'free'
export const SUBSCRIPTION_OFFERINGS_AVAILABILITY_ONLY_WITH = 'onlyWith'

export function hasLiveAccessEntitlement (subscriptions) {
  return isEntitled(subscriptions) && subscriptions.includes(LIVE_ACCESS_SUBSCRIPTION_CODE)
}

export function hasPurchaseOnlyEntitlement (subscriptions) {
  return isEntitled(subscriptions) && subscriptions.includes(SUBSCRIPTION_CODE_PURCHASE_ONLY)
}

/**
 * Check if user has Oracle access based on subscription SKUs
 * Oracle access is granted to users with subscriptions: 200, 253, 280, 290
 * @param {List<number>} subscriptions - Immutable List of subscription SKUs
 * @returns {boolean}
 */
export function hasOracleAccessEntitlement (subscriptions) {
  if (!isEntitled(subscriptions)) {
    return false
  }
  return ORACLE_ACCESS_SUBSCRIPTION_CODES.some((sku) => subscriptions.includes(sku))
}

export function isEntitled (subscriptions = List()) {
  if (!List.isList(subscriptions)) {
    return false
  }

  return subscriptions.size > 0
}

export async function getUserEntitled (idExpires, subscriptions) {
  try {
    const serverTime = await getServerTime()
    const entitled = _gt(idExpires, serverTime.serverTime) && _gt(_size(subscriptions), 0)

    return entitled
  } catch (e) {
    return false
  }
}

/**
 * Filter out purchase only subscriptions.
 * @param {List<number>} subscriptions - Immutable List of subscription ids
 * @returns {List<number>} Immutable List of subscriptions without SUBSCRIPTION_CODE_PURCHASE_ONLY
 */
export function filterStandardSubscriptions (subscriptions = List()) {
  if (!List.isList(subscriptions)) {
    return List()
  }

  return subscriptions.filter((subscription) => subscription !== SUBSCRIPTION_CODE_PURCHASE_ONLY)
}

/**
 * Filter entitlement objects to only "standard" (exclude purchase-only by content_set_id).
 * Mirrors filterStandardSubscriptions for the entitlement object shape.
 * @param {List} entitlements - List of entitlement objects
 * @returns {List} Immutable List of standard entitlement items
 */
export function filterStandardEntitlements (entitlements = List()) {
  if (!List.isList(entitlements)) {
    return List()
  }

  return entitlements.filter((entitlement) => {
    const contentSetId = entitlement.get('content_set_id')
    const ecommerceSku = entitlement.get('ecommerce_sku')
    return contentSetId !== SUBSCRIPTION_CODE_PURCHASE_ONLY && !ecommerceSku
  })
}

/**
 * Returns true if the user has at least one active standard subscription entitlement.
 * Uses filterStandardEntitlements first, then checks if any has not expired (end date).
 * @param {List} entitlements - List of entitlement objects (e.g. userEntitlements).
 *   Each item may be a plain object or Immutable Map with content_set_id and end.
 * @returns {Boolean}
 */
export function hasActiveStandardSubscription (entitlements = List()) {
  if (!List.isList(entitlements)) {
    return false
  }

  const standardEntitlements = filterStandardEntitlements(entitlements)
  const nowSec = Math.floor(Date.now() / 1000)

  return standardEntitlements.some((entitlement) => {
    const end = entitlement.get('end')
    if (end === null || end === undefined || end === '') return true
    const endSec = _parseInt(end, 10)
    if (Number.isNaN(endSec)) return true
    return endSec > nowSec
  })
}

export function isFeatureAllowedWithSubscription (feature, auth, isFirstEpisode = false) {
  const availability = feature.getIn(['offerings', 'availability'], null)
  const subscriptions = feature.getIn(['offerings', 'subscriptions'], List())
  const userSubscriptions = auth.get('subscriptions', List())

  if (availability === SUBSCRIPTION_OFFERINGS_AVAILABILITY_ONLY_WITH) {
    return subscriptions.some((v) => userSubscriptions.includes(v)) || isFirstEpisode
  }
  return featureIsFree(availability)
}

export function featureIsFree (availability) {
  return availability === SUBSCRIPTION_OFFERINGS_AVAILABILITY_IS_FREE
}
